import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { MentionsInput, Mention } from '@/components/react-mentions';
import { getMentions } from '@/components/react-mentions/utils';
import markupToRegex from '@/components/react-mentions/utils/markupToRegex';
import { useAgentStore, useChatInputStore } from '@/store';
import { getMentionColor } from '@/constants/Colors';
import { CHAT_INPUT_STYLE, AGENT_URL_PREFIX } from '@/constants/config';
import useTranslation from '@/hooks/useTranslation';
import { observeTextareaWidth } from './textareaLayout';

const createMentionStyle = (colors, chatInputStyle) => ({
  height: '100%',
  control: {
    backgroundColor: 'transparent',
    fontSize: 'inherit',
    fontFamily: 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif',
    position: 'relative',
    minHeight: 45,
    height: '100%',
    overflowY: 'auto',
  },
  input: {
    top: 0,
    left: 0,
    width: '100%',
    border: 'none',
    bottom: 0,
    height: '100%',
    minHeight: 45,
    maxHeight: '100%',
    margin: 0,
    resize: 'none',
    display: 'block',
    paddingBottom: 12,
    paddingTop: 
      chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? 12 : 
      chatInputStyle === CHAT_INPUT_STYLE.FIXED ? 15 : 12,
    paddingRight: 
      chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? 12 : 
      chatInputStyle === CHAT_INPUT_STYLE.FIXED ? 12 : 12,
    paddingLeft: 
      chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? 16 : 
      chatInputStyle === CHAT_INPUT_STYLE.FIXED ? 5 : 16,
    overflow: 'auto',
    position: 'absolute',
    fontSize: 'inherit',
    boxSizing: 'border-box',
    fontFamily: 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif',
    letterSpacing: 'inherit',
    backgroundColor: 'transparent',
    color: colors.text_color_primary,
    outlineStyle: 'none',
    '&:focus': {
      outlineStyle: 'none',
      border: 'none',
    },
  },
  highlighter: {
    boxSizing: 'border-box',
    color: 'transparent',
    paddingBottom: 12,
    paddingTop: 
      chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? 12 : 
      chatInputStyle === CHAT_INPUT_STYLE.FIXED ? 15 : 12,
    paddingRight: 
      chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? 12 : 
      chatInputStyle === CHAT_INPUT_STYLE.FIXED ? 12 : 12,
    paddingLeft: 
      chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? 16 : 
      chatInputStyle === CHAT_INPUT_STYLE.FIXED ? 5 : 16,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    border: 'none',
    position: 'relative',
    minHeight: 45,
    height: '100%',
    fontFamily: 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif',
  },
  suggestions: {
    backgroundColor: colors.solid_color_primary,
    border: `1px solid ${colors.border_color_secondary}`,
    borderRadius: '8px',
    backdropFilter: 'blur(18px) saturate(1.8)',
    animationDuration: '350ms',
    animationTimingFunction: 'cubic-bezier(0.15, 1.15, 0.3, 1.35)',
    willChange: 'transform, opacity',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
    marginTop: 8,
    position: 'fixed',
    zIndex: 999999,
    width: 'auto',
    minWidth: 240,
    maxWidth: '100%',
    overflowY: 'auto',
    maxHeight: 300,
    padding: 8,
  },
});

const styles = StyleSheet.create({
  container: {
    // The composer owns the measured height. Growing this column flex child
    // can inflate an empty input to its maximum height after a viewport resize.
    flexGrow: 0,
    flexShrink: 1,
    width: '100%',
    minWidth: 0,
    minHeight: 30,
    height: '100%',
    maxHeight: 240,
    position: 'relative',
    overflow: 'hidden',
  },
  unmeasuredContainer: {
    flexGrow: 1,
    flexBasis: 0,
    height: 'auto',
  },
});

const createMentionMarkup = (colors, theme) => ({
  backgroundColor: getMentionColor(theme, colors),
  padding: 2,
  borderRadius: 4,
  fontFamily: 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif',
  fontWeight: '400',
  color: colors.text_color_primary,
  margin: -2,
});

const AGENT_MENTION_MARKUP = `@[__display__](${AGENT_URL_PREFIX}__id__)`;

// react-mentions inserts AGENT_MENTION_MARKUP into the value on pick. The
// tag no longer lives inline in the composer (AgentTagChip above the input
// owns visibility/removal), so the markup — which replaces the typed
// "@query" — is removed from the text entirely: the pill carries the
// targeting and the prompt stays clean.
const AGENT_MENTION_MARKUP_REGEX = /@\[([^\]]*)\]\(agent:\/\/[^)\s]+\)/g;
const plainTextFromChange = (text) => (
  typeof text === 'string' && text.includes('](agent://')
    ? text.replace(AGENT_MENTION_MARKUP_REGEX, '')
    : text
);

// Agent suggestions fetcher using the agent store
const getAgentSuggestions = (query, callback) => {
  const { 
    agents,
    featuredAgentIds,
    searchAgents,
  } = useAgentStore.getState();

  // If query is empty, show featured agents
  if (!query.trim()) {
    const featuredAgents = featuredAgentIds
      .map(id => agents[id])
      .filter(Boolean)
      .map(agent => ({
        id: agent.id,
        display: agent.name,
        isPlusOnly: agent?.flags?.isPlusOnly,
      }));
    callback(featuredAgents);
    return;
  }

  // Search for agents
  searchAgents(query).then(() => {
    const { searchResultIds, agents } = useAgentStore.getState();
    const searchResults = searchResultIds
      .map(id => agents[id])
      .filter(Boolean)
      .map(agent => ({
        id: agent.id,
        display: agent.name,
        isPlusOnly: agent?.flags?.isPlusOnly,
      }));
    callback(searchResults);
  }).catch(() => {
    // On error, fallback to local filtering of featured agents
    const featuredAgents = featuredAgentIds
      .map(id => agents[id])
      .filter(Boolean)
      .filter(agent => agent.name.toLowerCase().includes(query.toLowerCase()))
      .map(agent => ({
        id: agent.id,
        display: agent.name,
        isPlusOnly: agent?.flags?.isPlusOnly,
      }));
    callback(featuredAgents);
  });
};

export default function ChatInputTextarea({
  value,
  onChangeText,
  placeholder,
  inputRef,
  onKeyDown,
  onPasteFiles,
  onFocus,
  onBlur,
  colors,
  theme,
  isListening,
  onContentSizeChange,
  selectedAgents: externalSelectedAgents,
  onSetSelectedAgents,
  enableAgentMentions = false,
  chatInputStyle,
  autoFocus,
}) {
  const { t } = useTranslation();
  const [localSelectedAgents, setLocalSelectedAgents] = useState(externalSelectedAgents || []);
  const searchTimeoutRef = useRef(null);
  const agents = useAgentStore(state => state.agents);
  const featuredAgentIds = useAgentStore(state => state.featuredAgentIds);
  const featuredAgentsError = useAgentStore(state => state.featuredAgentsError);
  const isLoadingFeaturedAgents = useAgentStore(state => state.isLoadingFeaturedAgents);
  const isFetchedFeaturedAgents = useAgentStore(state => state.isFetchedFeaturedAgents);
  const fetchFeaturedAgents = useAgentStore(state => state.fetchFeaturedAgents);
  const resetSearch = useAgentStore(state => state.resetSearch);

  const mentionInputStyle = useMemo(() => createMentionStyle(colors, chatInputStyle), [colors, chatInputStyle]);
  const mentionStyle = useMemo(() => createMentionMarkup(colors, theme), [colors, theme]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !onContentSizeChange) return undefined;
    const el = inputRef?.current;
    const textarea = el?.tagName === 'TEXTAREA' ? el : el?.querySelector?.('textarea');
    if (!textarea) return undefined;
    return observeTextareaWidth(textarea, () => onContentSizeChange(textarea.scrollHeight));
  }, [inputRef, onContentSizeChange]);

  // console.log('localSelectedAgents, externalSelectedAgents  ', localSelectedAgents, externalSelectedAgents);

  // Fetch featured agents on mount if not already loaded
  useEffect(() => {
    if (!isLoadingFeaturedAgents && !isFetchedFeaturedAgents) {
      try {
        fetchFeaturedAgents();
      } catch (error) {
        console.error('Failed to fetch featured agents:', error);
      }
    }
  }, [fetchFeaturedAgents, isLoadingFeaturedAgents, isFetchedFeaturedAgents]);

  // Cleanup search when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      resetSearch();
    };
  }, [resetSearch]);

  // Update local state when external selectedAgents changes
  useEffect(() => {
    setLocalSelectedAgents(externalSelectedAgents || []);
  }, [externalSelectedAgents]);

  // The inline mention tag is gone: selection is shown (and removed) via the
  // AgentTagChip rendered by the composer, so there is no prefill to sync
  // here anymore. Legacy drafts may still hold markup; it is cleaned on the
  // next change via plainTextFromChange and stripped on send.

  // Debounced agent suggestions
  const getAgentSuggestionsDebounced = useCallback((query, callback) => {
    if (!enableAgentMentions) { callback([]); return; }
    // One bot per message (backend limit): once an agent is mentioned, stop
    // suggesting others for new "@" triggers. maxMentions={1} only blocks the
    // add — the popup would still open and offer agents it would then reject.
    if (localSelectedAgents.length > 0) { callback([]); return; }
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If query is empty, show featured agents immediately
    if (!query.trim()) {
      const featuredAgents = featuredAgentIds
        .map(id => agents[id])
        .filter(Boolean)
        .map(agent => ({
          id: agent.id,
          display: agent.name,
          isPlusOnly: agent?.flags?.isPlusOnly,
        }));
      callback(featuredAgents);
      return;
    }

    // Debounce the search
    searchTimeoutRef.current = setTimeout(() => {
      getAgentSuggestions(query, callback);
    }, 300);
  }, [agents, featuredAgentIds, enableAgentMentions, localSelectedAgents]);

  const renderSuggestion = useCallback((suggestion, search, highlightedDisplay, index, focused) => {
    return (
      <div style={{
        padding: '8px',
        height: '30px',
        borderRadius: '4px',
        boxSizing: 'border-box',
        color: colors.text_color_primary,
        cursor: 'pointer',
        backgroundColor: focused ? colors.solid_color_secondary : 'transparent',
        display: 'flex',
        alignItems: 'center',
        fontSize: 14,
        lineHeight: '30px',
      }}>
        {highlightedDisplay}
      </div>
    )
  }, [colors]);

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
  };

  const handleChange = (e, newValue) => {
    onChangeText?.(plainTextFromChange(newValue));
    
    // Calculate new height based on scrollHeight
    if (inputRef?.current) {
      const textarea = inputRef.current.querySelector('textarea');
      if (textarea) {
        // Reset height temporarily to get the correct scrollHeight
        textarea.style.height = '45px';
        const scrollHeight = textarea.scrollHeight;
        onContentSizeChange?.(scrollHeight);
      }
    }
  };

  // Automation / IME can set textarea.value without MentionsInput onChange.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const el = inputRef?.current;
    const textarea = el?.tagName === 'TEXTAREA' ? el : el?.querySelector?.('textarea');
    if (!textarea) return undefined;
    const onInput = () => {
      const next = String(textarea.value || '');
      if (next !== (value || '')) onChangeText?.(plainTextFromChange(next));
    };
    textarea.addEventListener('input', onInput);
    return () => {
      try { textarea.removeEventListener('input', onInput); } catch (_) { /* already gone */ }
    };
  }, [inputRef, onChangeText, value]);

  // Handle paste of files/screenshots from clipboard (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!onPasteFiles) return;

    const el = inputRef?.current; // MentionsInput sets this to the actual <textarea> or <input>
    if (!el) return;

    const handlePaste = (e) => {
      try {
        const clipboard = e.clipboardData || e.nativeEvent?.clipboardData;
        const items = clipboard && clipboard.items;
        if (!items || !items.length) return;
        const files = [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item && item.kind === 'file') {
            const file = item.getAsFile && item.getAsFile();
            if (file && file.size > 0) files.push(file);
          }
        }
        if (files.length > 0) {
          e.preventDefault();
          onPasteFiles?.(files);
        }
      } catch (_) {}
    };

    el.addEventListener('paste', handlePaste);
    return () => {
      try { el.removeEventListener('paste', handlePaste); } catch (_) {}
    };
  }, [inputRef, onPasteFiles]);

  const handleMentionDisplayTransform = useCallback((id, display) => {
    // Spacey spacer
    return ` @${display} `;
  }, []);

  const handleOnAdd = useCallback((id, display, startPos, endPos) => {
    console.log('handleOnAdd', id);
    if (!localSelectedAgents.some(agent => agent.id === id)) {
      const agent = agents[id];
      if (agent) {
        const newSelectedAgents = [...localSelectedAgents, agent];
        setLocalSelectedAgents(newSelectedAgents);
        onSetSelectedAgents?.(newSelectedAgents.map(agent => agent.id));
      }
    }
  }, [localSelectedAgents, agents, onSetSelectedAgents]);

  const handleOnRemove = useCallback((id, display, startPos, endPos) => {
    console.log('handleOnRemove', id);
    if (localSelectedAgents.some(agent => agent.id === id)) {
      const newSelectedAgents = localSelectedAgents.filter(agent => agent.id !== id);
      setLocalSelectedAgents(newSelectedAgents);
      onSetSelectedAgents?.(newSelectedAgents.map(agent => agent.id));
    }
  }, [localSelectedAgents, onSetSelectedAgents]);

  return (
    <View style={[styles.container, !onContentSizeChange && styles.unmeasuredContainer]}>
      <MentionsInput
        data-gab-composer="true"
        value={value}
        onChange={handleChange}
        style={mentionInputStyle}
        placeholder={isListening ? t('chatInput.listeningPlaceholder') : placeholder}
        singleLine={false}
        allowSuggestionsAboveCursor={true}
        forceSuggestionsAboveCursor={true}
        allowSpaceInQuery={true}
        // Escape composer overflow:hidden / backdrop-filter, which otherwise
        // clip the @ picker to a sliver on the top edge of the input.
        suggestionsPortalHost={typeof document !== 'undefined' ? document.body : undefined}
        inputRef={inputRef}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
        autoFocus={autoFocus}
      >
        <Mention
          trigger="@"
          data={getAgentSuggestionsDebounced}
          style={mentionStyle}
          renderSuggestion={renderSuggestion}
          appendSpaceOnAdd={true}
          maxMentions={1}
          onAdd={handleOnAdd}
          onRemove={handleOnRemove}
          markup={AGENT_MENTION_MARKUP}
          displayTransform={handleMentionDisplayTransform}
        />
      </MentionsInput>
    </View>
  );
}
